import {
  folderTypes,
  FolderTypesNames,
} from "@redotech/redo-model/views/folders";
import { ClaimView, ViewTypesNames } from "@redotech/redo-model/views/views";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { createContext, memo } from "react";
import { CreateReturnViewFolderModal } from "../../return/views/create-return-view-folder-modal";
import { EditReturnViewFolderModal } from "../../return/views/edit-return-view-folder-modal";
import { useViewOrderingService } from "../shared/view-ordering-service-base";
import { ClaimViewsContext, ReloadClaimViewsContext } from "./claim-views";

export interface ClaimViewOrderingData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: ClaimView[],
    updatedFolders: folderTypes[FolderTypesNames.CLAIMS][],
  ) => void;
  saveOrdering: () => void;
  views: ClaimView[];
  folders: folderTypes[FolderTypesNames.CLAIMS][];
  notificationCounts: Record<string, number>;
}

export const ClaimViewOrderingContext = createContext<
  ClaimViewOrderingData | undefined
>(undefined);

export const ClaimViewOrderingService = memo(function ClaimViewOrderingService({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    views,
    folders,
    notificationCounts,
    showCreateFolderModal,
    showEditFolderModal,
    selectedFolder,
    reservedFolderNames,
    handleCreateFolder,
    handleEditFolder,
    handleDeleteFolder,
    updateOrdering,
    saveOrdering,
    openCreateFolderModal,
    openEditFolderModal,
    setShowCreateFolderModal,
    setShowEditFolderModal,
  } = useViewOrderingService({
    returnType: ReturnTypeEnum.CLAIM,
    viewType: ViewTypesNames.CLAIMS,
    navDataContext: ClaimViewsContext,
    reloadNavDataContext: ReloadClaimViewsContext,
  });

  return (
    <ClaimViewOrderingContext.Provider
      value={{
        openCreateFolderModal,
        openEditFolderModal,
        handleDeleteFolder,
        updateOrdering,
        saveOrdering,
        views,
        folders,
        notificationCounts,
      }}
    >
      {children}
      <CreateReturnViewFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        onSubmit={handleCreateFolder}
        reservedFolderNames={reservedFolderNames}
      />
      {selectedFolder && (
        <EditReturnViewFolderModal
          deleteFolder={handleDeleteFolder}
          isOpen={showEditFolderModal}
          onClose={() => setShowEditFolderModal(false)}
          onSubmit={handleEditFolder}
          reservedFolderNames={reservedFolderNames}
          selectedFolder={selectedFolder}
        />
      )}
    </ClaimViewOrderingContext.Provider>
  );
});
